/**
 * One product-create attempt. The lock stays held until channel draft saves
 * settle. A failed attempt keeps its idempotency key for the retry.
 */

export interface SubmissionLock {
  inFlight: boolean;
  idemKey: string;
  /** Set once createProduct confirms a product. Create must not run again for this attempt. */
  productId: string | null;
}

export function beginSubmission(
  lock: SubmissionLock,
  freshKey: string,
): { accepted: boolean; lock: SubmissionLock } {
  if (lock.inFlight || lock.productId) return { accepted: false, lock };
  return {
    accepted: true,
    lock: { inFlight: true, idemKey: lock.idemKey || freshKey, productId: null },
  };
}

export function failSubmission(lock: SubmissionLock): SubmissionLock {
  return { inFlight: false, idemKey: lock.idemKey, productId: null };
}

export function bindCreatedProduct(lock: SubmissionLock, productId: string): SubmissionLock {
  return { inFlight: true, idemKey: lock.idemKey, productId };
}

/** Post-create failure: stop the spinner, keep the product, and do not offer another create. */
export function pauseAfterCreate(lock: SubmissionLock): SubmissionLock {
  return { inFlight: false, idemKey: lock.idemKey, productId: lock.productId };
}

export function finishSubmission(): SubmissionLock {
  return { inFlight: false, idemKey: "", productId: null };
}

export function abandonCreateAttempt(): SubmissionLock {
  return finishSubmission();
}

export function canDiscardDraft(lock: { inFlight: boolean; productId?: string | null }): boolean {
  return !lock.inFlight && !lock.productId;
}

export function successIdentity(snapshot: { name: string; sku: string | null }): { name: string; sku: string | null } {
  return { name: snapshot.name, sku: snapshot.sku };
}

/** A guard held for the duration of one mutating retry of a creation attempt. */
export interface RetryGate {
  current: boolean;
}

/**
 * B-009: every mutating recovery path for one creation attempt shares this gate,
 * so two retries can never run against the same attempt at once. It is released
 * in `finally`, which means a thrown error cannot leave the attempt locked.
 */
export async function runGuardedRetry(
  gate: RetryGate,
  lock: Pick<SubmissionLock, "inFlight">,
  work: () => Promise<void>,
): Promise<"ran" | "skipped"> {
  if (gate.current || lock.inFlight) return "skipped";
  gate.current = true;
  try {
    await work();
    return "ran";
  } finally {
    gate.current = false;
  }
}

/**
 * Monotonic marker for one wizard attempt. Async work started for attempt N
 * captures the epoch and must not commit state once the wizard has moved on to
 * attempt N+1 — a late callback must never overwrite a newly started product.
 */
export interface AttemptEpoch {
  value: number;
}

export function nextAttemptEpoch(epoch: AttemptEpoch): number {
  epoch.value += 1;
  return epoch.value;
}

export function attemptEpochIsCurrent(epoch: AttemptEpoch, seen: number): boolean {
  return epoch.value === seen;
}

/**
 * Commit an awaited retry result ONLY when it still belongs to the current
 * attempt.
 *
 * Every post-create handler must await its work into a local variable and pass
 * it through here — never assign an awaited value straight into a shared ref.
 * A late callback from an abandoned attempt (operator reset, new product) must
 * not overwrite the new attempt's channel outcomes or listing ids.
 *
 * Returns true when the value was committed.
 */
export function commitAttemptResult<T>(
  epoch: AttemptEpoch,
  seen: number,
  value: T,
  commit: (value: T) => void,
): boolean {
  if (!attemptEpochIsCurrent(epoch, seen)) return false;
  commit(value);
  return true;
}
